import React, { useMemo, useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import LoanDetails from './Forms/LoanDetails';
import ResponsiveTable from './ResponsiveTable';
import StatusBadge from './UI/StatusBadge';
import { useTableSort } from './UI/tableSort';
import { toCurrency } from '../utils/adminMetrics';
import { formatDateDMY } from '../utils/dateUtils';
import Pager from './UI/Pager';

const PAGE_SIZE = 3;

const fmtDate = (value) => {
  return formatDateDMY(value);
};

// One table row's figures. The API's figures: total_due is what is still owed
// (penalty charged included), state.total_paid what was repaid. Together they
// are the total repayable: principal + interest + expenses + penalty charged.
// A loan never paid out owes nothing (dashes).
const withFigures = (loan) => {
  const paidOut = Boolean(loan.state?.approved);
  const paid = Number(loan.state?.total_paid) || 0;
  const outstanding = paidOut && loan.total_due != null && Number.isFinite(Number(loan.total_due))
    ? Number(loan.total_due)
    : null;
  return {
    loan,
    id: loan.id,
    code: loan.loan_code || `Loan #${loan.id}`,
    status: loan.status,
    reason: loan.loan_reason_name,
    principal: loan.principal_amount,
    totalDue: outstanding === null ? null : outstanding + paid,
    paid: paidOut ? paid : null,
    outstanding,
    duration: loan.duration_months,
    period: `${fmtDate(loan.start_date)} – ${fmtDate(loan.due_date)}`,
  };
};

// A client's loans as a table; clicking a row opens the full Loan Details.
// clientName / clientCode: shown in the details (the loans list has no client fields).
const ClientLoanHistory = ({ clientLoans, loadingLoans, clientName = '', clientCode = '' }) => {
  const [openLoan, setOpenLoan] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchTerm]);

  const filteredAndSorted = useMemo(() => {
    const byStatus = statusFilter === 'all'
      ? clientLoans
      : clientLoans.filter(l => (l.status || '').toLowerCase() === statusFilter.toLowerCase());

    const bySearch = searchTerm.trim()
      ? byStatus.filter(l => {
          const haystack = [
            l.loan_reason_name,
            l.application_type,
            l.id,
            l.status
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(searchTerm.trim().toLowerCase());
        })
      : byStatus;

    // Sort by created_at desc (fallback to id desc)
    return [...bySearch].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (aDate === bDate) return (b.id || 0) - (a.id || 0);
      return bDate - aDate;
    });
  }, [clientLoans, statusFilter, searchTerm]);

  // The table's rows for every match, so a click on a column sorts them all
  // before the page is taken.
  const loanRows = useMemo(() => filteredAndSorted.map(withFigures), [filteredAndSorted]);
  const loanSort = useTableSort(loanRows);
  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / PAGE_SIZE));
  const pagedLoans = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return loanSort.rows.slice(start, start + PAGE_SIZE);
  }, [loanSort.rows, page]);

  return (
    <div className="col-span-2 text-sm">
      {loadingLoans ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600 dark:text-slate-400">Loading loan history...</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between bg-white dark:bg-slate-800 p-3 rounded border border-gray-100 dark:border-slate-700 shadow-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <label className="text-xs text-gray-600 dark:text-slate-400">Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                
              >
                <option value="all">All</option>
                <option value="Approved">Approved</option>
                <option value="Outstanding">Outstanding</option>
                <option value="Bad Debt">Bad Debt</option>
                <option value="Written off">Written off</option>
                <option value="Paid Off">Paid Off</option>
                <option value="Pending">Pending</option>
                <option value="Awaiting Client">Awaiting Client</option>
                <option value="Awaiting Staff">Awaiting Staff</option>
                <option value="Awaiting Approval">Awaiting Approval</option>
                <option value="Rejected">Rejected</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Terminated">Terminated</option>
              </select>
              <label className="text-xs text-gray-600 dark:text-slate-400 ml-2">Search</label>
              <input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Reason, type, id..."
                
              />
            </div>
            <Pager page={page} pages={totalPages} onPage={setPage} />
          </div>

          {pagedLoans.length > 0 ? (
            <ResponsiveTable
              data={pagedLoans}
              sort={loanSort.sort}
              onSortChange={loanSort.onSortChange}
              onRowClick={(row) => setOpenLoan(row.loan)}
              columns={[
                { field: 'code', header: 'Loan', formatter: (code) => <span className="font-semibold">{code}</span> },
                {
                  field: 'status',
                  header: 'Status',
                  formatter: (status) => (
                    <StatusBadge status={status} size="small" />
                  ),
                },
                { field: 'reason', header: 'Reason', formatter: (v) => v || '-' },
                { field: 'principal', header: 'Principal', formatter: (v) => toCurrency(v) },
                { field: 'totalDue', header: 'Total due', formatter: (v) => (v === null ? '-' : toCurrency(v)) },
                { field: 'paid', header: 'Paid', formatter: (v) => (v === null ? '-' : toCurrency(v)) },
                {
                  field: 'outstanding',
                  header: 'Outstanding',
                  formatter: (v) => (
                    <span className={`font-semibold ${v === null ? 'text-gray-500' : v > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {v === null ? '-' : toCurrency(v)}
                    </span>
                  ),
                },
                { field: 'duration', header: 'Duration', formatter: (v) => `${v} month${Number(v) === 1 ? '' : 's'}` },
                { field: 'period', header: 'Start – due', formatter: (v) => <span className="whitespace-nowrap">{v}</span> },
                {
                  field: 'id',
                  header: '',
                  formatter: () => (
                    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-blue-600 dark:text-blue-300 whitespace-nowrap">
                      View loan <ChevronRight size={14} />
                    </span>
                  ),
                },
              ]}
            />
          ) : (
            <div className="text-center py-8">
              <svg className="w-16 h-16 text-gray-400 dark:text-slate-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 dark:text-slate-400 text-lg">No loan history</p>
              <p className="text-gray-500 dark:text-slate-500 text-sm mt-2">Try adjusting filters or search.</p>
            </div>
          )}
        </div>
      )}
      {openLoan && (
        <LoanDetails
          loan={{ ...openLoan, clientName: openLoan.clientName || clientName, client_code: openLoan.client_code || clientCode }}
          onClose={() => setOpenLoan(null)}
        />
      )}
    </div>
  );
};

export default ClientLoanHistory;